import produce from 'immer';

import {
  ConfigSavingState,
  REVIEW_CRITERION_STATUS,
  ReviewCriteriaGroup,
  ReviewCriterion,
} from 'notebook/reviews/ReviewCriteriaConstants';
import {ReviewCriteriaActionType} from 'notebook/reviews/ReviewCriteriaReduxActions';
import {BenchlingAction} from 'redux-stores/Action';

export interface ReviewCriteriaConfigState {
  deletingGroupIds: Array<string>;
  newGroupsByLinkedItemId: {[linkedItemId: string]: ReviewCriteriaGroup};
  newGroupSavingStatesByLinkedItemId: {[linkedItemId: string]: ConfigSavingState | undefined};
  pendingScrollIntoViewByLinkedItemId: {[linkedItemId: string]: boolean};
  reviewCriteriaGroupEditsById: {[id: string]: ReviewCriteriaGroup};
  reviewCriteriaGroupSavingStatesById: {[id: string]: ConfigSavingState | undefined};
}

const REVIEW_CRITERIA_CONFIG_INITIAL_STATE = {
  deletingGroupIds: [],
  newGroupsByLinkedItemId: {},
  newGroupSavingStatesByLinkedItemId: {},
  pendingScrollIntoViewByLinkedItemId: {},
  reviewCriteriaGroupEditsById: {},
  reviewCriteriaGroupSavingStatesById: {},
};

const REVIEW_CRITERION_INITIAL_STATE: ReviewCriterion = {
  id: null,
  text: '',
  status: REVIEW_CRITERION_STATUS.UNREVIEWED,
};

function reduceDraft(state: ReviewCriteriaGroup, action: BenchlingAction): ReviewCriteriaGroup | undefined {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_ADD_CRITERION:
      return produce(state, (draftState) => {
        draftState.reviewCriteria.push(REVIEW_CRITERION_INITIAL_STATE);
      });
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_CRITERION: {
      const {rowIndex, text} = action;
      return produce(state, (draftState) => {
        draftState.reviewCriteria[rowIndex].text = text;
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_CRITERION: {
      const {rowIndex} = action;
      return produce(state, (draftState) => {
        draftState.reviewCriteria.splice(rowIndex, 1);
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_NAME: {
      const {name} = action;
      return produce(state, (draftState) => {
        draftState.name = name;
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP:
      if (state) {
        // A draft is already being edited, so don't overwrite it
        return state;
      }
      return {id: null, name: '', reviewCriteria: [REVIEW_CRITERION_INITIAL_STATE]};
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_EDIT:
      return action.reviewCriteriaGroup;
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CANCEL_EDIT:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED:
      // Delete the draft
      return undefined;
    default:
      return state;
  }
}

function reduceConfigGroupDrafts(
  state: ReviewCriteriaConfigState,
  action: BenchlingAction
): ReviewCriteriaConfigState {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_ADD_CRITERION:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CANCEL_EDIT:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_CRITERION:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_NAME:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_CRITERION:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_EDIT:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED: {
      const {linkedItemId} = action;
      const reviewCriteriaGroupId =
        // eslint-disable-next-line benchlint/no-in-operator
        'reviewCriteriaGroupId' in action ? action.reviewCriteriaGroupId : undefined;
      if (reviewCriteriaGroupId) {
        return produce(state, (draftState) => {
          draftState.reviewCriteriaGroupEditsById[reviewCriteriaGroupId] = reduceDraft(
            draftState.reviewCriteriaGroupEditsById[reviewCriteriaGroupId],
            action
          ) as ReviewCriteriaGroup;
        });
      } else {
        return produce(state, (draftState) => {
          draftState.newGroupsByLinkedItemId[linkedItemId] = reduceDraft(
            draftState.newGroupsByLinkedItemId[linkedItemId],
            action
          ) as ReviewCriteriaGroup;
        });
      }
    }
    default:
      return state;
  }
}

function reduceSavingState(
  state: ConfigSavingState | undefined,
  action: BenchlingAction
): ConfigSavingState | undefined {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_EDIT:
      return ConfigSavingState.EDITING;
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_CREATE:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_UPDATE:
      return ConfigSavingState.SAVING;
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_FAILED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_FAILED:
      return ConfigSavingState.EDITING;
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CANCEL_EDIT:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED:
      // Draft was deleted
      return undefined;
    default:
      return state;
  }
}

function reduceConfigGroupSavingStates(
  state: ReviewCriteriaConfigState,
  action: BenchlingAction
): ReviewCriteriaConfigState {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CANCEL_EDIT:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_FAILED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_CREATE:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_EDIT:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_UPDATE:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_FAILED: {
      const {linkedItemId} = action;
      const reviewCriteriaGroupId =
        // eslint-disable-next-line benchlint/no-in-operator
        'reviewCriteriaGroupId' in action ? action.reviewCriteriaGroupId : undefined;
      if (reviewCriteriaGroupId) {
        return produce(state, (draftState) => {
          draftState.reviewCriteriaGroupSavingStatesById[reviewCriteriaGroupId] = reduceSavingState(
            draftState.reviewCriteriaGroupSavingStatesById[reviewCriteriaGroupId],
            action
          );
        });
      } else {
        return produce(state, (draftState) => {
          draftState.newGroupSavingStatesByLinkedItemId[linkedItemId] = reduceSavingState(
            draftState.newGroupSavingStatesByLinkedItemId[linkedItemId],
            action
          );
        });
      }
    }
    default:
      return state;
  }
}

function reduceConfigGroupDeletingStates(
  state: ReviewCriteriaConfigState,
  action: BenchlingAction
): ReviewCriteriaConfigState {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_DELETE: {
      const {reviewCriteriaGroupId} = action;
      return produce(state, (draftState) => {
        draftState.deletingGroupIds.push(reviewCriteriaGroupId);
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_COMPLETED:
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_FAILED: {
      const {reviewCriteriaGroupId} = action;
      return produce(state, (draftState) => {
        draftState.deletingGroupIds = draftState.deletingGroupIds.filter(
          (id) => id !== reviewCriteriaGroupId
        );
      });
    }
    default:
      return state;
  }
}

function reducePendingScrollIntoView(
  state: ReviewCriteriaConfigState,
  action: BenchlingAction
): ReviewCriteriaConfigState {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP: {
      const {linkedItemId} = action;
      return produce(state, (draftState) => {
        draftState.pendingScrollIntoViewByLinkedItemId[linkedItemId] = true;
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_NEW_GROUP_SCROLLED_INTO_VIEW: {
      const {linkedItemId} = action;
      return produce(state, (draftState) => {
        draftState.pendingScrollIntoViewByLinkedItemId[linkedItemId] = false;
      });
    }
    default:
      return state;
  }
}

export default function reduceReviewCriteriaConfigUI(
  state: ReviewCriteriaConfigState = REVIEW_CRITERIA_CONFIG_INITIAL_STATE,
  action: BenchlingAction
): ReviewCriteriaConfigState {
  state = reduceConfigGroupDrafts(state, action);
  state = reduceConfigGroupSavingStates(state, action);
  state = reduceConfigGroupDeletingStates(state, action);
  state = reducePendingScrollIntoView(state, action);
  return state;
}
